/* The Atelier: the next page, after this one is finished (load, at-warmup idle, then an idle callback).
   The Safari path warms reusable photos and theme files; Chromium also gets document speculation. Product links carry
   the gallery renderer's own srcset and sizes; collections return the card renderer's first eight photos. Nothing
   is painted or laid out here. IntersectionObserver supplies positions, including for the home sections whose
   content-visibility keeps them asleep. A press moves a destination to the front; desktop hover waits 80 ms.
   One request at a time, low priority, 2 MB on phones/touch and 4 MB elsewhere. Timing bytes where exposed, estimates
   otherwise; the last image may finish above its reservation. Hidden tabs pause, pagehide stops, bfcache resumes.
   Debug / off: window.atNextpage; sessionStorage['at:nextpage:off'] = '1' and reload. */
(function () {
  'use strict';
  var conn = navigator.connection;
  if (conn && (conn.saveData || /(^|-)2g$/.test(conn.effectiveType || ''))) return;
  try { if (sessionStorage.getItem('at:nextpage:off') === '1') return; } catch (e) {}

  var config = document.currentScript.dataset;
  var phone = matchMedia('(max-width:800px), (hover:none)').matches;
  var state = (window.atNextpage = { bytes: 0, budget: phone ? 2e6 : 4e6, idle: false, log: [], skipped: [] });
  var productPage = document.body.classList.contains('template-product');
  var gridPage = document.body.matches('.template-collection, .template-search');
  var LINK = 'a[href*="/products/"], a[href*="/collections/"]';
  var EXCLUDE = '.at-sr, cart-drawer, footer, .at-footer, [inert], [aria-current="page"]';
  var positions = new WeakMap();
  var observed = new WeakSet();
  var links = [];
  var queue = [];
  var candidates = new Set();
  var photos = new Set();
  var files = new Map();
  var rules = [];
  var top = [];
  var planned = false;
  var loaded = document.readyState === 'complete';
  var busy = false;
  var cancel = null;
  var stopped = false;
  var stale = true;
  var intent = null;
  var hover = null;
  var assets = new Set();
  var recommendations = new WeakSet();
  var waiting = 0;
  // Safari 26.5 accepts the script type in this simulator but makes no document prefetches. Keep this enhancement
  // on Chromium as intended, still checking the API; Chrome on iOS (CriOS) uses WebKit and is left out too.
  var chromium = navigator.userAgentData
    ? navigator.userAgentData.brands.some(function (brand) { return brand.brand === 'Chromium'; })
    : /(?:Chrome|Chromium)\//.test(navigator.userAgent);
  var supportsRules = chromium && window.HTMLScriptElement && HTMLScriptElement.supports && HTMLScriptElement.supports('speculationrules');
  var idle = window.requestIdleCallback
    ? function (fn) { requestIdleCallback(fn); }
    : function (fn) { var ch = new MessageChannel(); ch.port1.onmessage = function () { ch.port1.close(); ch.port2.close(); fn(); }; ch.port2.postMessage(0); };

  function ready() {
    return !stopped && !document.hidden && loaded && window.atWarmup && window.atWarmup.idle;
  }

  function destination(a) {
    if (!a || a.download || (a.target && a.target !== '_self')) return null;
    var u = new URL(a.href, location.href);
    if (u.origin !== location.origin || u.pathname === location.pathname || u.searchParams.has('view')) return null;
    var kind = /\/products\/[^/]+\/?$/.test(u.pathname) ? 'product' : /\/collections\/[^/]+\/?$/.test(u.pathname) ? 'collection' : null;
    if (!kind) return null;
    u.hash = '';
    return { url: u.href, kind: kind, el: a };
  }

  function scan() {
    stale = false;
    var added = false;
    links = Array.from(document.querySelectorAll(LINK)).filter(function (a) { return !!destination(a); });
    links.forEach(function (a) {
      if (observed.has(a)) return;
      observed.add(a);
      added = true;
      io.observe(a);
    });
    if (!added) schedule();
  }

  // Read boxes from the observer, never getBoundingClientRect: off-screen home sections must stay skipped.
  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      var r = e.boundingClientRect;
      positions.set(e.target, {
        shown: r.width > 0 && r.height > 0,
        intersects: e.isIntersecting,
        distance: Math.max(0, r.top - innerHeight, -r.bottom) + Math.max(0, r.left - innerWidth, -r.right),
        nextRow: r.bottom > 0 && r.top < innerHeight + r.height && r.left < innerWidth && r.right > 0,
      });
    });
    schedule();
  }, { rootMargin: '100% 0px' });

  function rank(a, index) {
    var d = destination(a);
    var pos = positions.get(a);
    if (!d || !pos || !pos.shown || a.closest(EXCLUDE) || a.closest('details:not([open]), [hidden]')) return null;
    if (!a.closest('main') && (!pos.intersects || pos.distance)) return null;
    if (d.kind === 'product' && !a.closest('[data-next-srcset]')) return null;
    if (gridPage && d.kind === 'product' && !pos.nextRow) return null;
    d.tier = d.kind === 'collection' ? 4 : productPage ? (a.closest('at-pairs') ? 0 : a.closest('product-recommendations') ? 1 : a.closest('.at-colour') ? 2 : 3) : 0;
    d.distance = pos.distance;
    d.index = index;
    return d;
  }

  function add(d, front) {
    if (!d || candidates.has(d.url)) return;
    candidates.add(d.url);
    var jobs = [];
    if (d.kind === 'product') {
      var meta = d.el.closest('[data-next-srcset]');
      // A variant query may choose a different first photo from the card's default product metadata.
      if (meta && !new URL(d.url).searchParams.has('variant')) jobs.push({ kind: 'photo', srcset: meta.dataset.nextSrcset, sizes: meta.dataset.nextSizes, page: d.url });
    } else {
      var u = new URL(d.url);
      u.searchParams.set('view', 'nextpage');
      jobs.push({ kind: 'collection', url: u.href, page: d.url });
    }
    jobs.forEach(function (job) { job.tier = front ? -1 : d.tier; });
    // Pairs/recommendations can arrive after the first scan. They still precede collection photos waiting in line.
    var at = front ? 0 : queue.findIndex(function (job) { return job.tier > d.tier; });
    if (at < 0) at = queue.length;
    queue.splice.apply(queue, [at, 0].concat(jobs));
    assets.add(d.kind);
  }

  function populate() {
    var ranked = links.map(rank).filter(Boolean).sort(function (a, b) { return a.tier - b.tier || a.distance - b.distance || a.index - b.index; });
    var seen = new Set();
    ranked = ranked.filter(function (d) { if (seen.has(d.url)) return false; seen.add(d.url); return true; });
    // Leave room for the next collection and its styles too, rather than spending the whole budget on a long rail.
    ranked.filter(function (d) { return d.kind === 'product'; }).slice(0, phone ? 4 : 8).forEach(function (d) { add(d); });
    ranked.filter(function (d) { return d.kind === 'collection'; }).slice(0, phone ? 2 : 3).forEach(function (d) { add(d); });
    if (!top.length) top = ranked.slice(0, 4).map(function (d) { return d.url; });
  }

  function themeFiles() {
    assets.forEach(function (kind) {
      (config[kind + 'Assets'] || '').split(',').filter(Boolean).forEach(function (url) {
        url = new URL(url, location.href).href;
        if (!files.has(url) && !queue.some(function (j) { return j.url === url; })) queue.push({ kind: 'asset', url: url, tier: 5 });
      });
    });
    assets.clear();
  }

  function account(job, url, estimate, failed) {
    var entry = performance.getEntriesByName(url).pop() || files.get(url);
    var measurable = entry && typeof entry.transferSize === 'number' && (new URL(url, location.href).origin === location.origin || entry.responseStart > 0);
    var bytes = measurable ? entry.transferSize : estimate;
    state.bytes += bytes;
    state.log.push({ kind: job.kind, page: job.page, url: url, bytes: bytes, estimated: !measurable, failed: !!failed });
    files.set(url, entry || {});
  }

  function warmPhoto(job) {
    var key = job.srcset + '|' + job.sizes;
    if (!job.srcset || !job.sizes || photos.has(key)) return Promise.resolve();
    photos.add(key);
    var same = Array.from(document.images).some(function (img) {
      return img.srcset === job.srcset && img.sizes === job.sizes && img.complete && img.naturalWidth;
    });
    if (same) return Promise.resolve();
    return new Promise(function (resolve) {
      var img = new Image();
      img.decoding = 'async';
      img.fetchPriority = 'low';
      img.onload = img.onerror = function (e) {
        cancel = null;
        // naturalWidth is density-corrected for srcset; the CDN width is the file's pixel width, not its CSS width.
        var width = Number(new URL(img.currentSrc || location.href).searchParams.get('width')) || 1100;
        var ratio = img.naturalWidth ? img.naturalHeight / img.naturalWidth : 1.25;
        account(job, img.currentSrc, Math.round(width * width * ratio * 0.2), e.type === 'error');
        resolve();
      };
      cancel = function () {
        img.onload = img.onerror = null;
        img.removeAttribute('srcset');
        photos.delete(key);
        queue.unshift(job);
        resolve();
      };
      // sizes first: WebKit can otherwise choose 100vw before the intended gallery/grid width arrives.
      img.sizes = job.sizes;
      img.srcset = job.srcset;
    });
  }

  async function warmFile(job) {
    if (files.has(job.url)) return;
    var controller = new AbortController();
    cancel = function () { controller.abort(); };
    try {
      var response = await fetch(job.url, { priority: 'low', signal: controller.signal });
      var body = await response.text();
      account(job, job.url, new Blob([body]).size, !response.ok);
      if (!response.ok || job.kind !== 'collection') return;
      var data = JSON.parse(body);
      if (!Array.isArray(data.photos)) throw new Error('Invalid collection photos');
      var jobs = data.photos.slice(0, phone ? 4 : 8).filter(function (p) {
        return p && typeof p.srcset === 'string' && typeof p.sizes === 'string';
      }).map(function (p) { return { kind: 'photo', srcset: p.srcset, sizes: p.sizes, page: job.page, tier: job.tier }; });
      queue.unshift.apply(queue, jobs);
    } catch (e) {
      if (controller.signal.aborted) queue.unshift(job);
      else state.skipped.push({ url: job.url, reason: 'request or collection response failed' });
    } finally {
      cancel = null;
    }
  }

  function speculate() {
    if (!supportsRules || rules.length || !top.length) return;
    // The browser owns speculation (including its concurrency and bytes); report estimated document bytes separately.
    // Search's result rows already have rules in at-search.js, and are excluded from both rules here.
    var navigation = performance.getEntriesByType('navigation')[0];
    var estimate = top.length * ((navigation && navigation.encodedBodySize) || 100000);
    var selector = 'a[href*="/products/"], a[href*="/collections/"]';
    var where = { and: [{ href_matches: location.origin + '/*' }, { selector_matches: selector }, { not: { selector_matches: '.at-sr a, [download], [target="_blank"]' } }] };
    var script = document.createElement('script');
    script.type = 'speculationrules';
    script.textContent = JSON.stringify({
      prefetch: [{ where: where, eagerness: 'moderate' }, { urls: top, eagerness: 'immediate' }],
      prerender: [{ where: { and: [where, { selector_matches: '.at-card a[href]' }] }, eagerness: 'conservative' }],
    });
    document.head.appendChild(script);
    rules.push(script);
    state.speculationEstimate = estimate;
  }

  function summary() {
    if (state.idle) return;
    state.idle = true;
    console.debug('[at-nextpage] ' + state.log.length + ' resources, ' + Math.round(state.bytes / 1024) + ' KB; skipped:', state.skipped);
  }

  function recommendationsBusy() {
    // The photo queue can empty while its recommendations fetch is still returning the cards. Let those land first.
    document.querySelectorAll('product-recommendations').forEach(function (el) {
      if (!el.recsLoad || recommendations.has(el.recsLoad)) return;
      recommendations.add(el.recsLoad);
      waiting++;
      var done = function () { waiting--; schedule(); };
      el.recsLoad.then(done, done);
    });
    return waiting > 0;
  }

  function pump() {
    if (!ready() || busy) return;
    if (recommendationsBusy()) return;
    if (stale) { scan(); return; } // Observer delivery supplies positions before candidate selection.
    if (intent) { add(intent, true); intent = null; }
    populate();
    if (!queue.length) themeFiles();
    var job = queue.shift();
    if (!job) { speculate(); summary(); return; }
    var estimate = job.kind === 'photo' ? 300000 : job.kind === 'collection' ? 16000 : 120000;
    if (state.bytes + estimate > state.budget) {
      state.skipped.push({ url: job.page || job.url, reason: 'budget' });
      schedule();
      return;
    }
    busy = true;
    state.idle = false;
    if (!state.firstAt) state.firstAt = Math.round(performance.now());
    (job.kind === 'photo' ? warmPhoto(job) : warmFile(job)).finally(function () { busy = false; schedule(); });
  }

  function schedule() {
    if (planned || !ready()) return;
    planned = true;
    idle(function () { planned = false; pump(); });
  }

  function promote(a) {
    var d = destination(a);
    if (!d) return;
    var matching = queue.filter(function (job) { return job.page === d.url; });
    matching.forEach(function (job) { job.tier = -1; });
    queue = matching.concat(queue.filter(function (job) { return job.page !== d.url; }));
    intent = d;
    schedule();
  }

  ['pointerdown', 'touchstart'].forEach(function (event) {
    document.addEventListener(event, function (e) { promote(e.target.closest && e.target.closest(LINK)); }, { passive: true });
  });
  if (matchMedia('(hover:hover) and (pointer:fine)').matches) {
    document.addEventListener('pointerover', function (e) {
      var a = e.target.closest && e.target.closest(LINK);
      if (!a || (e.relatedTarget && a.contains(e.relatedTarget))) return;
      clearTimeout(hover);
      hover = setTimeout(function () { promote(a); }, 80);
    }, { passive: true });
    document.addEventListener('pointerout', function (e) {
      var a = e.target.closest && e.target.closest(LINK);
      if (a && (!e.relatedTarget || !a.contains(e.relatedTarget))) clearTimeout(hover);
    }, { passive: true });
  }

  var mo = new MutationObserver(function (records) {
    if (!records.some(function (r) { return r.addedNodes.length || r.removedNodes.length; })) return;
    stale = true;
    schedule();
  });
  var po = new PerformanceObserver(function (list) {
    list.getEntries().forEach(function (entry) { files.set(entry.name, entry); });
  });
  po.observe({ type: 'resource', buffered: true });
  function watch() { mo.observe(document.body, { childList: true, subtree: true }); }
  function dropRules() { rules.splice(0).forEach(function (script) { script.remove(); }); }
  function moved() {
    // Within the observer's margin a scroll need not cross a threshold. Ask for fresh positions once it ends.
    stale = true;
    observed = new WeakSet();
    io.disconnect();
    schedule();
  }
  watch();
  addEventListener('load', function () { loaded = true; schedule(); });
  addEventListener('scrollend', moved, { capture: true, passive: true });
  addEventListener('resize', moved, { passive: true });
  document.addEventListener('at:warmup-done', schedule);
  document.addEventListener('visibilitychange', function () { if (document.hidden) dropRules(); else schedule(); });
  addEventListener('pagehide', function () {
    stopped = true;
    if (cancel) { cancel(); cancel = null; }
    clearTimeout(hover);
    dropRules();
    mo.disconnect();
    io.disconnect();
    po.disconnect();
  });
  addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    stopped = false;
    stale = true;
    observed = new WeakSet();
    watch();
    po.observe({ type: 'resource', buffered: true });
    schedule();
  });
  schedule();
})();
